import type { ReactElement, ReactNode } from "react";
import { createPortal } from "react-dom";
import { create } from "zustand";

/**
 * Lets a route put its own controls in the global bar's right zone.
 *
 * The bar already renders that zone on every route and it was always empty,
 * while pages that needed one persistent control had to grow a second header
 * row to hold it. The project page was the clearest case: a full identity row
 * whose only occupant was the Project Lead badge, stacked directly under the
 * breadcrumb that already named the page.
 *
 * A portal rather than a published value, unlike the breadcrumb's
 * `usePublishBreadcrumbLabel`. That one carries a string; this carries live
 * elements with handlers and their own subscriptions, and re-rendering them
 * where they are declared keeps them owned by the page instead of by the bar.
 */
interface AppTopBarActionSlotState {
  readonly container: HTMLElement | null;
  readonly setContainer: (container: HTMLElement | null) => void;
}

/** Module-private: the slot is reached through the two components below, never
 *  by subscribing to the container element directly. */
const useAppTopBarActionSlotStore = create<AppTopBarActionSlotState>((set) => ({
  container: null,
  setContainer: (container) => set({ container }),
}));

/**
 * Mounted once by `AppTopBar`. Registering on mount and clearing on unmount
 * keeps the store honest across the shells that render their own bar (login,
 * pairing, the crash boundary), so a portal never targets a detached node.
 */
export function AppTopBarActionSlotTarget(): ReactElement {
  const setContainer = useAppTopBarActionSlotStore((store) => store.setContainer);

  return (
    <div
      className="flex min-w-0 items-center gap-1.5"
      data-testid="app-top-bar-action-slot"
      ref={setContainer}
    />
  );
}

/**
 * Renders `children` into the bar for as long as the caller is mounted.
 *
 * Nothing renders until the target exists, which covers both SSR and the frame
 * between a route mounting and the bar registering its node.
 */
export function AppTopBarActions({
  children,
}: {
  readonly children: ReactNode;
}): ReactElement | null {
  const container = useAppTopBarActionSlotStore((store) => store.container);
  return container === null ? null : createPortal(children, container);
}
